import React, { useState } from 'react';
import { useScrollLock } from '../hooks/useScrollLock';
import { CategorySubcategorySelect } from './CategorySubcategorySelect';

interface ConflictTransaction {
  id: number;
  date: string;
  amount: number;
  description: string;
  merchant_name: string | null;
}

interface ConflictRule {
  id: number;
  rule_name: string;
  category_name: string;
  confidence: number;
}

interface CategorizationConflict {
  transaction: ConflictTransaction;
  rule: ConflictRule;
  suggestedCategory: {
    category_id: number;
    subcategory_id: number;
  };
}

interface CategorizationConflictModalProps {
  isOpen: boolean;
  onClose: () => void;
  conflicts: CategorizationConflict[];
  onResolve: (action: 'apply_rules' | 'categorize_manually' | 'leave_uncategorized' | 'mark_as_refunds', categoryId?: number, subcategoryId?: number) => Promise<void>;
  categories: any[];
}

const CategorizationConflictModal: React.FC<CategorizationConflictModalProps> = ({
  isOpen,
  onClose,
  conflicts,
  onResolve,
  categories
}) => {
  useScrollLock(isOpen);
  
  const [selectedAction, setSelectedAction] = useState<'apply_rules' | 'categorize_manually' | 'leave_uncategorized' | 'mark_as_refunds'>('mark_as_refunds');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<number | null>(null);
  const [isResolving, setIsResolving] = useState(false);

  if (!isOpen) return null;

  const handleResolve = async () => {
    setIsResolving(true);
    try {
      const transactionIds = conflicts.map(c => c.transaction.id);
      await onResolve(selectedAction, selectedCategoryId || undefined, selectedSubcategoryId || undefined);
      onClose();
    } catch (error) {
      console.error('Error resolving conflicts:', error);
    } finally {
      setIsResolving(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount);
  };

  const getTransactionType = (amount: number) => {
    return amount > 0 ? 'Income' : 'Expense';
  };

  const getRuleType = (categoryName: string) => {
    const incomeCategories = ['Employment', 'Business', 'Investment', 'Other Income'];
    const expenseCategories = ['Housing', 'Food', 'Transportation', 'Healthcare', 'Entertainment', 'Shopping', 'Family', 'Charitable Giving', 'Other'];
    
    if (incomeCategories.includes(categoryName)) return 'Income';
    if (expenseCategories.includes(categoryName)) return 'Expense';
    return 'Unknown';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-white">
              🔄 Categorization Conflicts
            </h2>
            <p className="text-gray-400 mt-1">
              Found {conflicts.length} transaction{conflicts.length !== 1 ? 's' : ''} that match rules but are of a different type
            </p>
          </div>
          <button
            className="text-gray-400 hover:text-white text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-800 transition-colors"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Conflict Summary */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">Conflicting Transactions</h3>
            <div className="space-y-3">
              {conflicts.map((conflict, index) => (
                <div key={index} className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="text-white font-medium">{conflict.transaction.description}</div>
                      <div className="text-gray-400 text-sm">
                        {new Date(conflict.transaction.date).toLocaleDateString()} • 
                        {conflict.transaction.merchant_name && ` ${conflict.transaction.merchant_name} •`}
                        <span className={`ml-1 ${conflict.transaction.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {formatCurrency(conflict.transaction.amount)}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-400">
                        Transaction: <span className="font-medium">{getTransactionType(conflict.transaction.amount)}</span>
                      </div>
                      <div className="text-sm text-gray-400">
                        Rule: <span className="font-medium">{getRuleType(conflict.rule.category_name)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-700 rounded p-3">
                    <div className="text-sm text-gray-300">
                      <strong>Rule:</strong> {conflict.rule.rule_name} → {conflict.rule.category_name}
                    </div>
                    <div className="text-sm text-gray-400 mt-1">
                      Confidence: {Math.round(conflict.rule.confidence * 100)}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Resolution Options */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">How would you like to handle these transactions?</h3>
            
            <div className="space-y-4">
              {/* Option 1: Apply Rules Anyway */}
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="resolution"
                  value="apply_rules"
                  checked={selectedAction === 'apply_rules'}
                  onChange={(e) => setSelectedAction(e.target.value as any)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="text-white font-medium">Apply rules anyway (recommended for refunds)</div>
                  <div className="text-gray-400 text-sm">
                    Use the suggested categories even though they don't match the transaction type. 
                    This is perfect for refunds that should reduce the original expense category.
                  </div>
                </div>
              </label>

              {/* Option 2: Manual Categorization */}
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="resolution"
                  value="categorize_manually"
                  checked={selectedAction === 'categorize_manually'}
                  onChange={(e) => setSelectedAction(e.target.value as any)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="text-white font-medium">Choose different categories</div>
                  <div className="text-gray-400 text-sm">
                    Manually select appropriate categories for these transactions.
                  </div>
                </div>
              </label>

              {/* Option 3: Mark as Refunds */}
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="resolution"
                  value="mark_as_refunds"
                  checked={selectedAction === 'mark_as_refunds'}
                  onChange={(e) => setSelectedAction(e.target.value as any)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="text-white font-medium">Mark as refunds</div>
                  <div className="text-gray-400 text-sm">
                    Categorize these as refunds (income transactions). This is recommended for refunds.
                  </div>
                </div>
              </label>
              
              {/* Option 4: Leave Uncategorized */}
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="resolution"
                  value="leave_uncategorized"
                  checked={selectedAction === 'leave_uncategorized'}
                  onChange={(e) => setSelectedAction(e.target.value as any)}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="text-white font-medium">Leave uncategorized</div>
                  <div className="text-gray-400 text-sm">
                    Don't categorize these transactions for now. You can handle them later.
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Manual Category Selection */}
          {selectedAction === 'categorize_manually' && (
            <div className="mb-6">
              <h4 className="text-md font-semibold text-white mb-3">Select Categories</h4>
              <CategorySubcategorySelect
                categories={categories}
                currentCategoryId={selectedCategoryId}
                currentSubcategoryId={selectedSubcategoryId}
                onChange={(categoryId, subcategoryId) => {
                  setSelectedCategoryId(categoryId);
                  setSelectedSubcategoryId(subcategoryId);
                }}
                isIncome={conflicts.length > 0 && conflicts[0].transaction.amount > 0}
                isRefund={true}
                placeholder="Choose appropriate categories..."
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            disabled={isResolving}
          >
            Cancel
          </button>
          <button
            onClick={handleResolve}
            disabled={isResolving || (selectedAction === 'categorize_manually' && !selectedCategoryId)}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isResolving ? 'Resolving...' : 'Resolve Conflicts'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategorizationConflictModal;
